import type { Meeting, MeetingSummary, PluginActionMessage, ToolView } from "@scoach/types";
import { useToast } from "@scoach/ui";
import { useEffect, useState } from "react";

import { PluginHost } from "../../plugins/PluginHost.tsx";
import { pluginsApi } from "../../plugins/api.ts";
import { buildSummaryContext } from "../../plugins/bridge.ts";
import { openPluginUrl } from "../../plugins/openPluginUrl.ts";

/**
 * Renders the user's enabled, approved SUMMARY-surface plugins on the summary
 * screen, each with read-only (scoped) summary context + the bridge.
 */
export function SummaryPluginsPanel({
  meetingId,
  summary,
  meeting,
}: {
  meetingId: string;
  summary: MeetingSummary | null;
  meeting: Meeting | null;
}) {
  const toast = useToast();
  const [plugins, setPlugins] = useState<ToolView[] | null>(null);

  useEffect(() => {
    pluginsApi
      .list("summary")
      .then((r) => setPlugins(r.plugins.filter((p) => p.userEnabled)))
      .catch(() => setPlugins([]));
  }, []);

  function handleAction(plugin: ToolView, msg: PluginActionMessage) {
    if (msg.action === "open-url") {
      openPluginUrl(msg.payload?.url);
    } else {
      toast.push({ tone: "info", message: `${plugin.name}: ${msg.action}` });
    }
  }

  const ctxFor = (p: ToolView) =>
    buildSummaryContext({
      scope: p.dataScope ?? "metadata",
      meetingId,
      company: meeting?.account?.name,
      meetingType: meeting?.meetingType,
      summary,
    });

  if (plugins === null) return <div className="tasks-empty">Loading plugins…</div>;
  if (plugins.length === 0) {
    return (
      <div className="tasks-empty" style={{ padding: 24, textAlign: "center" }}>
        No plugins enabled for the summary. Enable approved plugins in the{" "}
        <strong>Marketplace</strong>, or build your own in the <strong>Developer hub</strong>.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16 }}>
      {plugins.map((p) => (
        <div key={p.id} style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
            {p.icon ? `${p.icon} ` : ""}
            {p.name}
            <span style={{ fontWeight: 500, color: "var(--text-3)", marginLeft: 6 }}>
              · {p.dataScope === "full" ? "full context" : "metadata"}
            </span>
          </div>
          <PluginHost
            plugin={p}
            context={ctxFor(p)}
            onAction={(m) => handleAction(p, m)}
            height={460}
          />
        </div>
      ))}
    </div>
  );
}
